import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import { insightApi } from "../utils/scoreSvcApiNew.js";

export const useInsightStore = defineStore("insight", () => {
  const status = shallowRef(null);
  const overview = shallowRef(null);
  const behavior = shallowRef(null);
  const pareto = shallowRef(null);
  const rfm = shallowRef(null);
  const growthBase = shallowRef(null);
  const growthQuality = shallowRef(null);
  const revenueBand = shallowRef(null);
  const loading = ref(false);
  const error = ref(null);
  const sectionLoading = ref({
    overview: false,
    behavior: false,
    pareto: false,
    rfm: false,
    growthBase: false,
    growthQuality: false,
    revenueBand: false,
  });
  const sectionErrors = ref({
    overview: null,
    behavior: null,
    pareto: null,
    rfm: null,
    growthBase: null,
    growthQuality: null,
    revenueBand: null,
  });

  // NOTE: the old `get(path, extra)` helper is gone — insight is POST + JSON body now, and
  // every endpoint has a named method on `insightApi` that knows its own body shape.

  // No health/status endpoint is documented for insight on the new service
  // (the old engine had open /insight/{live,ready,status}). Nothing in the UI reads
  // `status`, so this resolves to a stub instead of firing a request that would 404.
  async function fetchStatus() {
    status.value = { service: "credit-score-service", status: "UNKNOWN", note: "no status endpoint in Credit-Score-Service" };
    return status.value;
  }

  async function fetchOverview() {
    loading.value = true;
    error.value = null;
    try {
      overview.value = await insightApi.overview();
      return overview.value;
    } catch (e) {
      error.value = e.message;
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function loadDashboard() {
    loading.value = true;
    error.value = null;
    await Promise.all([
      loadSection("overview", () => insightApi.overview(), (data) => {
        overview.value = data;
      }),
      loadSection("behavior", () => insightApi.behavior(new Date().getFullYear()), (data) => {
        behavior.value = data;
      }),
      loadSection("pareto", () => insightApi.pareto(new Date().getFullYear()), (data) => {
        pareto.value = data;
      }),
      loadSection("rfm", () => insightApi.rfm(), (data) => {
        rfm.value = data;
      }),
      loadSection("growthBase", () => insightApi.growthBase("month"), (data) => {
        growthBase.value = data;
      }),
      loadSection("growthQuality", () => insightApi.growthQuality("month"), (data) => {
        growthQuality.value = data;
      }),
      loadSection("revenueBand", () => insightApi.revenueByBand("month"), (data) => {
        revenueBand.value = data;
      }),
    ]);
    error.value = sectionErrors.value.overview;
    loading.value = false;
  }

  async function setGrowthPeriod(period, month) {
    await loadSection("growthBase", () => insightApi.growthBase(period, month), (data) => {
      growthBase.value = data;
    });
  }

  async function setQualityPeriod(period, month) {
    await loadSection("growthQuality", () => insightApi.growthQuality(period, month), (data) => {
      growthQuality.value = data;
    });
  }

  async function setRevenuePeriod(period) {
    await loadSection("revenueBand", () => insightApi.revenueByBand(period), (data) => {
      revenueBand.value = data;
    });
  }

  async function setBehaviorScope(year) {
    await loadSection("behavior", () => insightApi.behavior(year), (data) => {
      behavior.value = data;
    });
  }

  async function setParetoScope(year) {
    await loadSection("pareto", () => insightApi.pareto(year), (data) => {
      pareto.value = data;
    });
  }

  async function fetchAssetBreakdown(txType, startDate, endDate, customerType) {
    return insightApi.assetBreakdown(txType, startDate, endDate, customerType);
  }

  // Opening transaction of every customer whose first-ever transaction falls in [startDate, endDate]
  // (inclusive) — backs the "ประวัติธุรกรรมลูกค้าใหม่" page. Server-side paged. `filters` may carry
  // pawnValueOperator (EQUAL/MORE_THAN/LESS_THAN/BETWEEN) + pawnValue + toPawnValue + assetType.
  async function fetchNewCustomerHistory(startDate, endDate, page = 0, size = 50, filters = {}) {
    return insightApi.newCustomerHistory(startDate, endDate, page, size, filters);
  }

  // Distinct asset types in scope — options for the "ประเภทหลัก" filter dropdown.
  async function fetchInsightAssetTypes() {
    return insightApi.assetTypes();
  }

  
  // Monthly counts of transactions on existing tickets by timing against the due date — backs the
  // "Customer Behavior" page, which rolls months up into quarters / years itself.
  async function fetchDueBehavior() {
    return insightApi.dueBehavior();
  }

  // Every ticket by the month it falls due and what became of it — the ticket-cohort section of
  // Customer Behavior.
  async function fetchDueCohort() {
    return insightApi.dueCohort();
  }

  async function loadSection(key, request, assign) {
    setSectionState(sectionLoading, key, true);
    setSectionState(sectionErrors, key, null);
    try {
      const data = await request();
      assign(data);
      return data;
    } catch (e) {
      setSectionState(sectionErrors, key, e.message);
      if (key === "overview") error.value = e.message;
      return null;
    } finally {
      setSectionState(sectionLoading, key, false);
    }
  }

  function setSectionState(target, key, value) {
    target.value = {
      ...target.value,
      [key]: value,
    };
  }

  function sectionStatus(key) {
    return {
      loading: !!sectionLoading.value[key],
      error: sectionErrors.value[key],
    };
  }

  function hasSectionData(key) {
    switch (key) {
      case "overview":
        return !!overview.value;
      case "behavior":
        return !!behavior.value;
      case "pareto":
        return !!pareto.value;
      case "rfm":
        return !!rfm.value;
      case "growthBase":
        return !!growthBase.value;
      case "growthQuality":
        return !!growthQuality.value;
      case "revenueBand":
        return !!revenueBand.value;
      default:
        return false;
    }
  }

  return {
    status,
    overview,
    behavior,
    pareto,
    rfm,
    growthBase,
    growthQuality,
    revenueBand,
    loading,
    error,
    sectionLoading,
    sectionErrors,
    fetchStatus,
    fetchOverview,
    loadDashboard,
    setGrowthPeriod,
    setQualityPeriod,
    setRevenuePeriod,
    setBehaviorScope,
    setParetoScope,
    sectionStatus,
    hasSectionData,
    fetchAssetBreakdown,
    fetchNewCustomerHistory,
    fetchInsightAssetTypes,
    fetchDueBehavior,
    fetchDueCohort,
    api: insightApi,
  };
});
